import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  CaretLeft, 
  CaretRight, 
  DownloadSimple, 
  Clock,
  Users,
  ChartBar,
  Lock,
  Warning,
  Trophy
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const STATUS_LABELS = {
  abierto: { label: 'Abierto', color: 'bg-emerald-100 text-emerald-800' },
  en_cierre: { label: 'En Cierre', color: 'bg-amber-100 text-amber-800' },
  cerrado: { label: 'Cerrado', color: 'bg-red-100 text-red-800' },
};

const VALID_HOURS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];

const formatHours = (hours) => {
  if (!hours && hours !== 0) return '';
  if (hours % 1 === 0) return String(Math.floor(hours));
  return String(hours).replace('.', ',');
};

const OvertimePage = () => {
  const { axiosInstance, isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gridData, setGridData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  
  const [operativeMonth, setOperativeMonth] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editHours, setEditHours] = useState('');
  const [editObservation, setEditObservation] = useState('');
  const [allowEditPastDays, setAllowEditPastDays] = useState(true);
  
  const scrollContainerRef = useRef(null);

  const loadOperativeMonth = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/operative-month');
      setOperativeMonth(response.data);
      
      if (!isAdmin) {
        setCurrentYear(response.data.year);
        setCurrentMonth(response.data.month);
        setCanEdit(response.data.status !== 'cerrado');
      } else {
        setCanEdit(true);
      }
      return response.data;
    } catch (error) {
      // error handled silently
      return null;
    }
  }, [axiosInstance, isAdmin]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBranch) params.branch_id = selectedBranch;
      
      const [gridRes, branchesRes, settingsRes] = await Promise.all([
        axiosInstance.get(`/api/overtime/month/${currentYear}/${currentMonth}`, { params }),
        isAdmin ? axiosInstance.get('/api/branches') : Promise.resolve({ data: [] }),
        axiosInstance.get('/api/settings'),
      ]);
      
      setGridData(gridRes.data);
      setBranches(branchesRes.data);
      setAllowEditPastDays(settingsRes.data?.allow_edit_past_days ?? true);
      
      if (operativeMonth && !isAdmin) {
        const isOperativeMonth = currentYear === operativeMonth.year && currentMonth === operativeMonth.month;
        setCanEdit(isOperativeMonth && operativeMonth.status !== 'cerrado');
      }
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAdmin, currentYear, currentMonth, selectedBranch, operativeMonth]);

  useEffect(() => {
    const init = async () => {
      const opMonth = await loadOperativeMonth();
      if (opMonth && !isAdmin) {
        setCurrentYear(opMonth.year);
        setCurrentMonth(opMonth.month);
      }
    };
    init();
  }, [loadOperativeMonth, isAdmin]);

  useEffect(() => {
    if (operativeMonth) {
      loadData();
    }
  }, [loadData, operativeMonth, currentYear, currentMonth]);

  const scrollToToday = useCallback(() => {
    if (scrollContainerRef.current) {
      const todayDate = new Date();
      const todayMonth = todayDate.getMonth() + 1;
      const todayYear = todayDate.getFullYear();
      
      if (currentMonth === todayMonth && currentYear === todayYear) {
        const todayDay = todayDate.getDate();
        const containerWidth = scrollContainerRef.current.clientWidth;
        const fixedColumnsWidth = 336;
        const dayColumnWidth = 56;
        const todayColumnStart = fixedColumnsWidth + (todayDay - 1) * dayColumnWidth;
        const scrollPosition = todayColumnStart - (containerWidth / 2) + (dayColumnWidth / 2);
        scrollContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
      } else {
        scrollContainerRef.current.scrollLeft = 0;
      }
    }
  }, [currentMonth, currentYear]);

  useEffect(() => {
    if (gridData) {
      setTimeout(scrollToToday, 100);
    }
  }, [gridData, scrollToToday]);

  const handlePrevMonth = () => {
    if (!isAdmin) return;
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (!isAdmin) return;
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleCellClick = (worker, dateStr, currentData) => {
    // Block Z cells (not hired yet)
    if (currentData?.blocked) {
      return;
    }
    
    if (!canEdit) {
      toast.error('No puede editar registros. El mes operativo está cerrado.');
      return;
    }
    
    if (!isAdmin && operativeMonth) {
      const recordYear = parseInt(dateStr.substring(0, 4));
      const recordMonth = parseInt(dateStr.substring(5, 7));
      if (recordYear !== operativeMonth.year || recordMonth !== operativeMonth.month) {
        toast.error('Solo puede editar registros del mes operativo vigente.');
        return;
      }
    }

    // Block past days for non-admin when setting is disabled
    if (!isAdmin && !allowEditPastDays) {
      const todayStr = new Date().toISOString().split('T')[0];
      if (dateStr < todayStr) {
        toast.error('La edicion de dias anteriores esta desactivada por administracion.');
        return;
      }
    }
    
    setEditingCell({ worker, dateStr, currentData });
    setEditHours(currentData?.hours != null ? String(currentData.hours) : '');
    setEditObservation(currentData?.observation || '');
    setShowEditModal(true);
  };

  const handleSaveCell = async () => {
    if (!editingCell || !editHours) return;
    
    const hours = parseFloat(editHours.replace(',', '.'));
    if (isNaN(hours) || hours < 0 || hours % 0.5 !== 0) {
      toast.error('Valor inválido. Use múltiplos de 0,5 (ej: 0.5, 1, 1.5, 2)');
      return;
    }
    
    setSaving(true);
    try {
      await axiosInstance.post('/api/overtime', {
        worker_id: editingCell.worker.id,
        date: editingCell.dateStr,
        hours: hours,
        observation: editObservation || null,
      });
      setShowEditModal(false);
      toast.success('Horas extras registradas');
      await loadData();
      setTimeout(scrollToToday, 150);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCell = async () => {
    if (!editingCell) return;
    
    setSaving(true);
    try {
      await axiosInstance.delete(`/api/overtime/${editingCell.worker.id}/${editingCell.dateStr}`);
      setShowEditModal(false);
      toast.success('Registro eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', currentYear);
      params.append('month', currentMonth);
      params.append('format', 'xlsx');
      if (selectedBranch) params.append('branch_id', selectedBranch);
      
      const response = await axiosInstance.get(`/api/export/overtime-grid?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `horas_extras_${currentYear}_${String(currentMonth).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación descargada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const getDayOfWeek = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay();
  };

  const isWeekend = (year, month, day) => {
    const dow = getDayOfWeek(year, month, day);
    return dow === 0 || dow === 6;
  };

  const isToday = (dateStr) => {
    return dateStr === today.toISOString().split('T')[0];
  };

  const getCellBackground = (dayData, weekend, todayCell) => {
    // Z cells - blocked, not hired yet
    if (dayData?.blocked) {
      return 'bg-zinc-100 cursor-not-allowed';
    }
    
    let base = 'transition-colors';
    
    if (canEdit) {
      base += ' cursor-pointer hover:bg-zinc-100';
    } else {
      base += ' cursor-not-allowed';
    }
    
    if (todayCell) {
      base += ' ring-2 ring-zinc-900 ring-inset';
    }
    
    if (weekend) {
      base += ' bg-zinc-50';
    }
    
    if (dayData && dayData.hours > 0) {
      if (dayData.hours <= 1) {
        base += ' bg-sky-50';
      } else if (dayData.hours <= 2) {
        base += ' bg-sky-100';
      } else {
        base += ' bg-sky-200';
      }
    }
    
    return base;
  };

  const isViewingOperativeMonth = operativeMonth && 
    currentYear === operativeMonth.year && 
    currentMonth === operativeMonth.month;

  if (loading && !gridData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const summary = gridData?.summary || {};
  const daysInMonth = gridData?.days_in_month || 30;

  return (
    <div className="space-y-4" data-testid="overtime-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="overtime-title">
            Horas Extras
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isAdmin ? 'Control mensual de horas extras por trabajador' : 'Registro de horas extras de su sucursal'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && branches.length > 0 && (
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="form-select w-48"
              data-testid="overtime-branch-filter"
            >
              <option value="">Todas las sucursales</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleExport}
            className="btn-secondary flex items-center gap-2"
            data-testid="export-overtime-btn"
          >
            <DownloadSimple size={18} />
            Exportar
          </button>
        </div>
      </div>

      {/* Operative Month Status Banner (for non-admins) */}
      {!isAdmin && operativeMonth && (
        <div className={`p-4 border ${operativeMonth.status === 'cerrado' ? 'border-red-200 bg-red-50' : operativeMonth.status === 'en_cierre' ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <div className="flex items-center gap-3">
            {operativeMonth.status === 'cerrado' ? (
              <Lock size={20} className="text-red-600" />
            ) : operativeMonth.status === 'en_cierre' ? (
              <Warning size={20} className="text-amber-600" />
            ) : null}
            <div>
              <p className="font-medium text-zinc-900">
                Mes Operativo: {MONTHS[operativeMonth.month - 1]} {operativeMonth.year}
              </p>
              <p className="text-sm text-zinc-600">
                Estado: <span className={`px-2 py-0.5 text-xs ${STATUS_LABELS[operativeMonth.status]?.color}`}>
                  {STATUS_LABELS[operativeMonth.status]?.label}
                </span>
                {operativeMonth.status === 'cerrado' && ' - Solo lectura'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-sky-600" />
            <span className="kpi-label">Horas Hoy</span>
          </div>
          <div className="kpi-value text-xl" data-testid="today-hours">{formatHours(summary.today?.hours || 0)}</div>
          <p className="text-xs text-zinc-500">{summary.today?.workers_count || 0} trabajadores</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-zinc-600" />
            <span className="kpi-label">Horas Semana</span>
          </div>
          <div className="kpi-value text-xl" data-testid="week-hours">{formatHours(summary.week?.hours || 0)}</div>
          <p className="text-xs text-zinc-500">{summary.week?.count || 0} registros</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <ChartBar size={16} className="text-zinc-600" />
            <span className="kpi-label">Horas Mes</span>
          </div>
          <div className="kpi-value text-xl" data-testid="month-hours">{formatHours(summary.month?.hours || 0)}</div>
          <p className="text-xs text-zinc-500">{summary.month?.count || 0} registros</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} className="text-blue-600" />
            <span className="kpi-label">Trabajadores</span>
          </div>
          <div className="kpi-value text-xl" data-testid="unique-workers">{summary.month?.unique_workers || 0}</div>
          <p className="text-xs text-zinc-500">con horas extras</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <ChartBar size={16} className="text-amber-600" />
            <span className="kpi-label">Promedio Semanal</span>
          </div>
          <div className="kpi-value text-xl">{formatHours(summary.month?.avg_weekly || 0)}</div>
          <p className="text-xs text-zinc-500">horas/semana</p>
        </div>
        {summary.top_workers?.length > 0 && (
          <div className="kpi-card">
            <div className="flex items-center gap-2 mb-1">
              <Trophy size={16} className="text-amber-600" />
              <span className="kpi-label">Top Horas Extra</span>
            </div>
            <div className="text-sm font-medium text-zinc-900 truncate" data-testid="top-worker">
              {summary.top_workers[0]?.name}
            </div>
            <p className="text-xs text-zinc-500">{formatHours(summary.top_workers[0]?.hours)} hrs</p>
          </div>
        )}
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4 py-2">
        {isAdmin ? (
          <>
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-zinc-100 transition-colors"
              data-testid="ot-prev-month-btn"
            >
              <CaretLeft size={24} />
            </button>
            <h2 className="font-heading text-xl font-semibold text-zinc-900 min-w-[200px] text-center">
              {MONTHS[currentMonth - 1]} {currentYear}
              {isViewingOperativeMonth && (
                <span className={`ml-2 text-xs px-2 py-0.5 ${STATUS_LABELS[operativeMonth?.status]?.color}`}>
                  {STATUS_LABELS[operativeMonth?.status]?.label}
                </span>
              )}
            </h2>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-zinc-100 transition-colors"
              data-testid="ot-next-month-btn"
            >
              <CaretRight size={24} />
            </button>
            <button
              onClick={() => {
                if (operativeMonth) {
                  setCurrentMonth(operativeMonth.month);
                  setCurrentYear(operativeMonth.year);
                  setTimeout(scrollToToday, 200);
                }
              }}
              className="ml-4 px-3 py-1 text-sm bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
              data-testid="ot-go-to-operative-btn"
            >
              Mes Operativo
            </button>
            <button
              onClick={() => {
                const now = new Date();
                setCurrentMonth(now.getMonth() + 1);
                setCurrentYear(now.getFullYear());
                setTimeout(scrollToToday, 200);
              }}
              className="px-3 py-1 text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors"
              data-testid="ot-go-to-today-btn"
            >
              Hoy
            </button>
          </>
        ) : (
          <h2 className="font-heading text-xl font-semibold text-zinc-900">
            {MONTHS[currentMonth - 1]} {currentYear}
          </h2>
        )}
      </div>

      {/* Warning for non-operative month */}
      {isAdmin && !isViewingOperativeMonth && operativeMonth && (
        <div className="p-3 border border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-center gap-2">
          <Warning size={18} />
          <span>
            Está viendo un mes diferente al operativo actual ({MONTHS[operativeMonth.month - 1]} {operativeMonth.year}).
          </span>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1">
          <span className="w-6 h-6 flex items-center justify-center bg-sky-50 border border-sky-200 text-sky-700 font-mono font-bold">0,5</span>
          <span className="text-zinc-600">Baja</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-6 h-6 flex items-center justify-center bg-sky-100 border border-sky-200 text-sky-800 font-mono font-bold">1-2</span>
          <span className="text-zinc-600">Media</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-6 h-6 flex items-center justify-center bg-sky-200 border border-sky-300 text-sky-900 font-mono font-bold">3+</span>
          <span className="text-zinc-600">Alta</span>
        </div>
        {!canEdit && (
          <div className="flex items-center gap-1 ml-4 text-red-600">
            <Lock size={14} />
            <span>Solo lectura</span>
          </div>
        )}
      </div>

      {/* Grid Table */}
      <div className="border border-zinc-200 bg-white">
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto"
          style={{ maxHeight: 'calc(100vh - 480px)' }}
        >
          <table className="w-full border-collapse" data-testid="overtime-grid-table">
            <thead className="sticky top-0 z-10 bg-zinc-50">
              <tr>
                <th className="sticky left-0 z-20 bg-zinc-50 border-b border-r border-zinc-200 px-2 py-2 text-left text-xs uppercase tracking-wider font-medium text-zinc-500 w-16 min-w-[64px]">
                  Código
                </th>
                <th className="sticky left-16 z-20 bg-zinc-50 border-b border-r border-zinc-200 px-2 py-2 text-left text-xs uppercase tracking-wider font-medium text-zinc-500 w-48 min-w-[192px]">
                  Nombre
                </th>
                <th className="sticky left-64 z-20 bg-zinc-50 border-b border-r border-zinc-200 px-2 py-2 text-right text-xs uppercase tracking-wider font-medium text-zinc-500 w-16">
                  Total
                </th>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const weekend = isWeekend(currentYear, currentMonth, day);
                  const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const todayCell = isToday(dateStr);
                  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
                  const dow = getDayOfWeek(currentYear, currentMonth, day);
                  
                  return (
                    <th 
                      key={day}
                      className={`border-b border-r border-zinc-200 px-1 py-1 text-center text-xs font-medium w-14 min-w-[56px] ${weekend ? 'bg-zinc-100 text-zinc-400' : 'text-zinc-500'} ${todayCell ? 'bg-zinc-900 text-white' : ''}`}
                    >
                      <div className="font-bold">{day}</div>
                      <div className="text-[10px]">{dayNames[dow]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {gridData?.workers?.map((worker) => {
                const isInactive = worker.status === 'desvinculado';
                
                return (
                  <tr 
                    key={worker.id}
                    className={`${isInactive ? 'bg-zinc-50 opacity-60' : ''}`}
                  >
                    <td className={`sticky left-0 z-10 ${isInactive ? 'bg-zinc-50' : 'bg-white'} border-b border-r border-zinc-200 px-2 py-1 text-xs font-mono text-zinc-600 w-16 min-w-[64px]`}>
                      {worker.code}
                    </td>
                    <td className={`sticky left-16 z-10 ${isInactive ? 'bg-zinc-50' : 'bg-white'} border-b border-r border-zinc-200 px-2 py-1 w-48 min-w-[192px]`}>
                      <div className={`text-sm font-medium leading-tight ${isInactive ? 'text-zinc-400' : 'text-zinc-900'}`}>
                        {worker.first_name} {worker.last_name}
                      </div>
                      {worker.cargo && worker.cargo !== 'Sin cargo' && (
                        <span className="text-[10px] text-zinc-400">{worker.cargo}</span>
                      )}
                    </td>
                    <td className={`sticky left-64 z-10 ${isInactive ? 'bg-zinc-50' : 'bg-white'} border-b border-r border-zinc-200 px-2 py-1 text-right`}>
                      <span className={`text-xs font-bold font-mono ${worker.total_hours > 0 ? 'text-sky-700' : 'text-zinc-300'}`}>
                        {worker.total_hours > 0 ? formatHours(worker.total_hours) : '-'}
                      </span>
                    </td>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day = i + 1;
                      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const dayData = worker.days[dateStr];
                      const weekend = isWeekend(currentYear, currentMonth, day);
                      const todayCell = isToday(dateStr);
                      
                      return (
                        <td 
                          key={dateStr}
                          onClick={() => handleCellClick(worker, dateStr, dayData)}
                          className={`border-b border-r border-zinc-200 px-1 py-1 text-center w-14 min-w-[56px] h-10 ${getCellBackground(dayData, weekend, todayCell)}`}
                          data-testid={`ot-cell-${worker.id}-${dateStr}`}
                        >
                          {dayData?.blocked ? (
                            <span className="text-xs font-bold text-zinc-300" title="Trabajador aún no ingresaba a la empresa">Z</span>
                          ) : dayData && dayData.hours > 0 ? (
                            <span className="font-mono text-xs font-bold text-sky-800">
                              {formatHours(dayData.hours)}
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Cell Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-sm rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Horas Extras
            </DialogTitle>
            <p className="text-sm text-zinc-500">
              {editingCell?.worker?.first_name} {editingCell?.worker?.last_name} - {editingCell?.dateStr}
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="form-label">Horas Extras</label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {VALID_HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setEditHours(String(h))}
                    className={`p-2 border text-sm font-mono font-medium transition-colors ${
                      editHours === String(h)
                        ? 'border-sky-600 bg-sky-50 text-sky-800'
                        : 'border-zinc-200 hover:border-zinc-300 text-zinc-700'
                    }`}
                    data-testid={`hours-btn-${h}`}
                  >
                    {formatHours(h)}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={editHours}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setEditHours(val);
                  }
                }}
                className="form-input font-mono text-center text-lg"
                placeholder="0,5"
                data-testid="overtime-hours-input"
              />
              <p className="text-xs text-zinc-400 mt-1">Múltiplos de 0,5 (ej: 0,5 / 1 / 1,5 / 2)</p>
            </div>

            <div>
              <label className="form-label">Observación (opcional)</label>
              <textarea
                value={editObservation}
                onChange={(e) => setEditObservation(e.target.value)}
                rows={2}
                className="form-input resize-none"
                placeholder="Agregar nota..."
                data-testid="overtime-observation-input"
              />
            </div>

            <DialogFooter className="flex justify-between">
              <div>
                {editingCell?.currentData && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteCell}
                    disabled={saving}
                    className="rounded-none"
                    data-testid="delete-overtime-btn"
                  >
                    Eliminar
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="rounded-none"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveCell}
                  disabled={saving || !editHours}
                  className="rounded-none bg-zinc-900"
                  data-testid="save-overtime-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OvertimePage;
